import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { Observable, catchError, map, of, retry, shareReplay } from 'rxjs';
import { ApiService } from '../../core/services/api.service';

/**
 * The Salary module's chart-of-account (COA) codes. They are NOT typed here: hr-service
 * owns them (internal/features/salarycoa/salarycoa.go) and serves them from
 * GET hr/salary-coa. `salaryCoaGuard` loads them before any salary, voucher or
 * workflow screen opens, so to change a code edit that Go file and restart hr-service.
 *
 * These are `let` exports on purpose: ES module imports are live bindings, so every
 * `import { SALARY_PAYABLE_COA }` sees the value applySalaryCoa() sets. Read them at
 * use time (in methods / constructors), never copy them into module-level constants.
 */

/** Salary Payable — settled by salary, DA arrear, ACP arrear and salary arrear debit vouchers. */
export let SALARY_PAYABLE_COA = '';
export let SALARY_PAYABLE_COA_NAME = 'Salary Payable';
/** LIC Payable — settled by the Life Insurance (LIC) debit voucher. */
export let LIC_PAYABLE_COA = '';
export let LIC_PAYABLE_COA_NAME = 'LIC Payable';
/** EWF Payable — settled by the EWF debit voucher; '' while hr-service has none configured. */
export let EWF_PAYABLE_COA = '';
export let EWF_PAYABLE_COA_NAME = 'EWF Payable';
/** Income Tax Payable — settled by the Income Tax debit voucher. */
export let INCOME_TAX_PAYABLE_COA = '';
export let INCOME_TAX_PAYABLE_COA_NAME = 'Income Tax Payable';
/** Travel Allowance Payable — credited by the TA bill, settled by the TA debit voucher. */
export let TRAVEL_ALLOWANCE_PAYABLE_COA = '';
export let TRAVEL_ALLOWANCE_PAYABLE_COA_NAME = 'Travel Allowance Payable';
/** Medical Bill Payable — credited by the medical bill, settled by the medical debit voucher. */
export let MEDICAL_BILL_PAYABLE_COA = '';
export let MEDICAL_BILL_PAYABLE_COA_NAME = 'Medical Bill Payable';
/** Electricity Bill / HR Colony / Miscellaneous Deduction Payable — each settled by its own monthly debit voucher; '' until chosen on Salary COA Mapping. */
export let ELECTRICITY_BILL_PAYABLE_COA = '';
export let ELECTRICITY_BILL_PAYABLE_COA_NAME = 'Electricity Bill Payable';
export let HR_COLONY_PAYABLE_COA = '';
export let HR_COLONY_PAYABLE_COA_NAME = 'HR Colony Payable';
export let MISC_DEDUCTION_PAYABLE_COA = '';
export let MISC_DEDUCTION_PAYABLE_COA_NAME = 'Miscellaneous Deduction Payable';

interface CoaAccount {
  code: string;
  name: string;
}

export interface SalaryCoaConfig {
  salaryPayable: CoaAccount;
  licPayable: CoaAccount;
  ewfPayable: CoaAccount;
  incomeTaxPayable: CoaAccount;
  travelAllowancePayable?: CoaAccount;
  medicalBillPayable?: CoaAccount;
  electricityBillPayable?: CoaAccount;
  hrColonyPayable?: CoaAccount;
  miscDeductionPayable?: CoaAccount;
}

function applySalaryCoa(cfg: SalaryCoaConfig): void {
  SALARY_PAYABLE_COA = cfg.salaryPayable?.code ?? '';
  SALARY_PAYABLE_COA_NAME = cfg.salaryPayable?.name || SALARY_PAYABLE_COA_NAME;
  LIC_PAYABLE_COA = cfg.licPayable?.code ?? '';
  LIC_PAYABLE_COA_NAME = cfg.licPayable?.name || LIC_PAYABLE_COA_NAME;
  EWF_PAYABLE_COA = cfg.ewfPayable?.code ?? '';
  EWF_PAYABLE_COA_NAME = cfg.ewfPayable?.name || EWF_PAYABLE_COA_NAME;
  INCOME_TAX_PAYABLE_COA = cfg.incomeTaxPayable?.code ?? '';
  INCOME_TAX_PAYABLE_COA_NAME = cfg.incomeTaxPayable?.name || INCOME_TAX_PAYABLE_COA_NAME;
  TRAVEL_ALLOWANCE_PAYABLE_COA = cfg.travelAllowancePayable?.code ?? '';
  TRAVEL_ALLOWANCE_PAYABLE_COA_NAME = cfg.travelAllowancePayable?.name || TRAVEL_ALLOWANCE_PAYABLE_COA_NAME;
  MEDICAL_BILL_PAYABLE_COA = cfg.medicalBillPayable?.code ?? '';
  MEDICAL_BILL_PAYABLE_COA_NAME = cfg.medicalBillPayable?.name || MEDICAL_BILL_PAYABLE_COA_NAME;
  ELECTRICITY_BILL_PAYABLE_COA = cfg.electricityBillPayable?.code ?? '';
  ELECTRICITY_BILL_PAYABLE_COA_NAME = cfg.electricityBillPayable?.name || ELECTRICITY_BILL_PAYABLE_COA_NAME;
  HR_COLONY_PAYABLE_COA = cfg.hrColonyPayable?.code ?? '';
  HR_COLONY_PAYABLE_COA_NAME = cfg.hrColonyPayable?.name || HR_COLONY_PAYABLE_COA_NAME;
  MISC_DEDUCTION_PAYABLE_COA = cfg.miscDeductionPayable?.code ?? '';
  MISC_DEDUCTION_PAYABLE_COA_NAME = cfg.miscDeductionPayable?.name || MISC_DEDUCTION_PAYABLE_COA_NAME;
}

/** The in-flight or finished load; cleared on failure so the next navigation retries. */
let loaded$: Observable<boolean> | null = null;

/**
 * Loads the salary COA codes once per session before the route opens. A transient
 * failure is retried twice right away; a load that still fails doesn't block
 * navigation (the screens then report the COA as missing) and is retried on the
 * next navigation.
 */
export const salaryCoaGuard: CanActivateFn = () => {
  if (!loaded$) {
    loaded$ = inject(ApiService)
      .get<any>('hr/salary-coa')
      .pipe(
        retry({ count: 2, delay: 500 }),
        map((res: any) => {
          applySalaryCoa(res?.data as SalaryCoaConfig);
          return true;
        }),
        catchError((err) => {
          console.error('Could not load the salary chart-of-account codes', err);
          loaded$ = null;
          return of(true);
        }),
        shareReplay(1)
      );
  }
  return loaded$;
};
